// src/admin/pages/blogs/BlogManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Drawer,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
  InputAdornment,
  Pagination,
  Collapse,
  useTheme,
  useMediaQuery,
  Badge,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Article as ArticleIcon,
  CloudUpload as CloudUploadIcon,
  Cancel as CancelIcon,
  Publish as PublishIcon,
  DraftsOutlined as DraftIcon,
  Close as CloseIcon,
  DragIndicator as DragIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Comment as CommentIcon,
} from '@mui/icons-material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { Blog, BlogParagraph, BlogFormData } from './blog';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useBlogsPaginated,
  useBlogStats,
  useBlogCommentCounts,
  useBlogMutations,
} from '../../../hooks/useBlogs';
import ToastNotification from '../common/Toastnotification';
import BlogCommentManagement from './Blogcommentmanagement';

const BLOGS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.12)',  border: 'rgba(255,167,38,0.4)',   text: '#b76e00', iconColor: '#e68900' },
    info:      { bg: 'rgba(41,182,246,0.12)',   border: 'rgba(41,182,246,0.4)',   text: '#0277bd', iconColor: '#039be5' },
    primary:   { bg: 'rgba(33,150,243,0.12)',   border: 'rgba(33,150,243,0.4)',   text: '#1565c0', iconColor: '#1976d2' },
    success:   { bg: 'rgba(102,187,106,0.15)',  border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error:     { bg: 'rgba(239,83,80,0.12)',    border: 'rgba(239,83,80,0.4)',    text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)',   border: 'rgba(171,71,188,0.35)',  text: '#6a1b9a', iconColor: '#8e24aa' },
    default:   { bg: 'rgba(158,158,158,0.12)',  border: 'rgba(158,158,158,0.4)',  text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.22)',   border: 'rgba(255,167,38,0.55)',  text: '#ffcc80', iconColor: '#ffa726' },
    info:      { bg: 'rgba(41,182,246,0.20)',   border: 'rgba(41,182,246,0.50)',  text: '#81d4fa', iconColor: '#29b6f6' },
    primary:   { bg: 'rgba(33,150,243,0.22)',   border: 'rgba(33,150,243,0.50)',  text: '#90caf9', iconColor: '#42a5f5' },
    success:   { bg: 'rgba(102,187,106,0.22)',  border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error:     { bg: 'rgba(239,83,80,0.22)',    border: 'rgba(239,83,80,0.50)',   text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)',   border: 'rgba(171,71,188,0.50)',  text: '#ce93d8', iconColor: '#ab47bc' },
    default:   { bg: 'rgba(158,158,158,0.22)',  border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();

  const overlayBg =
    theme.palette.mode === 'dark'
      ? 'rgba(26, 29, 36, 0.85)'
      : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) {
        setRect(wrapperRef.current.getBoundingClientRect());
      }
    };

    measure();

    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);

    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        pointerEvents: 'all',
        borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading blogs...
        </Typography>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BlogManagement
// ─────────────────────────────────────────────────────────────────────────────
const BlogManagement: React.FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();

  // ── Permission flags ───────────────────────────────────────────────────────
  // isSuperAdmin bypasses all checks — all actions are visible and enabled.
  // For non-super-admin users, each action is gated by the relevant permission code.
  const canView            = isSuperAdmin || hasPermission('blog.view');
  const canCreate          = isSuperAdmin || hasPermission('blog.create');
  const canEdit            = isSuperAdmin || hasPermission('blog.edit');
  const canDelete          = isSuperAdmin || hasPermission('blog.delete');
  const canPublish         = isSuperAdmin || hasPermission('blog.publish');
  const canManageComments  = isSuperAdmin || hasPermission('blog.comments.manage');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
  const [formData, setFormData] = useState<BlogFormData>({
    title: '',
    authorName: '',
    paragraphs: [],
    tags: [],
    status: 'draft',
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [backgroundImage, setBackgroundImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [removeImage, setRemoveImage] = useState(false);
  const [currentTag, setCurrentTag] = useState('');

  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
  });

  const [deleteTarget, setDeleteTarget] = useState<Blog | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
  const [commentsBlog, setCommentsBlog] = useState<Blog | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const paragraphsContainerRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const saveAction = drawerMode === 'add' ? 'Creating Blog…' : 'Updating Blog…';

  React.useEffect(() => {
    if (paragraphsContainerRef.current && formData.paragraphs.length > 0) {
      paragraphsContainerRef.current.scrollTo({
        top: paragraphsContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [formData.paragraphs.length]);

  // ── React Query hooks ─────────────────────────────────────────────────────
  const {
    data: blogsData,
    isLoading: blogsLoading,
    isFetching: blogsIsFetching,
    isError: blogsIsError,
    error: blogsErrorObj,
  } = useBlogsPaginated({
    page: currentPage,
    search: searchQuery,
    status: statusFilter,
    pageSize: BLOGS_PER_PAGE,
  });

  const { data: statsData } = useBlogStats();

  const { create, update, remove, toggleStatus } = useBlogMutations();

  const blogs = blogsData?.blogs ?? [];
  const totalBlogs = blogsData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalBlogs / BLOGS_PER_PAGE);
  const totalPublished = statsData?.published ?? 0;
  const totalDrafts = statsData?.drafts ?? 0;
  const loading = blogsLoading || blogsIsFetching;

  const blogIds = blogs.map((b) => b.id!).filter(Boolean);
  const { data: commentCounts = {}, refetch: refetchCommentCounts } =
    useBlogCommentCounts(blogIds);

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Status filter ─────────────────────────────────────────────────────────
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  // ── Clear filters ─────────────────────────────────────────────────────────
  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    resetToFirstPage();
    showToast('Filters cleared', 'info');
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all'].filter(
    Boolean
  ).length;

  // ── Form helpers ──────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormData({
      title: '',
      authorName: '',
      paragraphs: [],
      tags: [],
      status: 'draft',
    });
    setSelectedBlog(null);
    setBackgroundImage(null);
    setImagePreview('');
    setRemoveImage(false);
    setCurrentTag('');
  };

  const handleAddBlog = () => {
    if (!canCreate) return;
    resetForm();
    setDrawerMode('add');
    setDrawerOpen(true);
  };

  const handleEditBlog = (blog: Blog) => {
    if (!canEdit) return;
    setFormData({
      title: blog.title,
      authorName: blog.authorName,
      paragraphs: blog.paragraphs,
      tags: blog.tags,
      status: blog.status,
    });
    setSelectedBlog(blog);
    setImagePreview(blog.backgroundImageUrl || '');
    setBackgroundImage(null);
    setRemoveImage(false);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    resetForm();
  };

  // ── Comments Drawer helpers ───────────────────────────────────────────────
  const handleViewComments = (blog: Blog) => {
    if (!canManageComments) return;
    setCommentsBlog(blog);
    setCommentsDrawerOpen(true);
  };

  const handleCloseCommentsDrawer = () => {
    setCommentsDrawerOpen(false);
    setCommentsBlog(null);
    if (blogIds.length > 0) refetchCommentCounts();
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteBlog = (blog: Blog) => {
    if (!canDelete) return;
    setDeleteTarget(blog);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      await remove.mutateAsync(deleteTarget.id!);
      setDeleteTarget(null);
      resetToFirstPage();
      showToast('Blog deleted successfully', 'success');
    } catch (err: any) {
      console.error('Error deleting blog:', err);
      showToast(`Failed to delete blog: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  // ── Toggle status ─────────────────────────────────────────────────────────
  const handleToggleStatus = async (blog: Blog) => {
    if (!canPublish) return;
    const newStatus = blog.status === 'draft' ? 'published' : 'draft';
    try {
      await toggleStatus.mutateAsync({ blogId: blog.id!, status: newStatus });
      resetToFirstPage();
      showToast(
        `Blog ${newStatus === 'published' ? 'published' : 'moved to draft'} successfully`,
        'success'
      );
    } catch (err: any) {
      console.error('Error toggling blog status:', err);
      showToast(
        `Failed to update blog status: ${err.message || 'Unknown error'}`,
        'error'
      );
    }
  };

  // ── Image helpers ─────────────────────────────────────────────────────────
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showToast('Please select an image file', 'error');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size should be less than 5MB', 'error');
        return;
      }
      setBackgroundImage(file);
      setRemoveImage(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview('');
    setBackgroundImage(null);
    setRemoveImage(true);
  };

  // ── Paragraph helpers ─────────────────────────────────────────────────────
  const handleAddParagraph = () => {
    const newParagraph: BlogParagraph = {
      id: Date.now().toString(),
      content: '',
      order: formData.paragraphs.length,
    };
    setFormData((prev) => ({
      ...prev,
      paragraphs: [...prev.paragraphs, newParagraph],
    }));
  };

  const handleParagraphChange = (id: string, content: string) => {
    setFormData((prev) => ({
      ...prev,
      paragraphs: prev.paragraphs.map((p) => (p.id === id ? { ...p, content } : p)),
    }));
  };

  const handleDeleteParagraph = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      paragraphs: prev.paragraphs
        .filter((p) => p.id !== id)
        .map((p, index) => ({ ...p, order: index })),
    }));
  };

  const handleMoveParagraph = (id: string, direction: 'up' | 'down') => {
    const index = formData.paragraphs.findIndex((p) => p.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === formData.paragraphs.length - 1) return;
    const newParagraphs = [...formData.paragraphs];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newParagraphs[index], newParagraphs[targetIndex]] = [
      newParagraphs[targetIndex],
      newParagraphs[index],
    ];
    setFormData((prev) => ({
      ...prev,
      paragraphs: newParagraphs.map((p, idx) => ({ ...p, order: idx })),
    }));
  };

  // ── Tag helpers ───────────────────────────────────────────────────────────
  const handleAddTag = () => {
    if (!currentTag.trim()) return;
    if (formData.tags.length >= 10) {
      showToast('Maximum 10 tags allowed', 'warning');
      return;
    }
    if (formData.tags.includes(currentTag.trim())) {
      showToast('Tag already exists', 'warning');
      return;
    }
    setFormData((prev) => ({ ...prev, tags: [...prev.tags, currentTag.trim()] }));
    setCurrentTag('');
  };

  const handleRemoveTag = (tag: string) => {
    setFormData((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  // ── Form submit ───────────────────────────────────────────────────────────
  const handleSubmit = async (status: 'draft' | 'published') => {
    // Guard: creating requires blog.create, editing requires blog.edit.
    // Publishing (status === 'published') additionally requires blog.publish.
    if (drawerMode === 'add' && !canCreate) return;
    if (drawerMode === 'edit' && !canEdit) return;
    if (status === 'published' && !canPublish) {
      showToast('You do not have permission to publish blogs', 'error');
      return;
    }

    if (!formData.title.trim()) {
      showToast('Blog title is required', 'error');
      return;
    }
    if (!formData.authorName.trim()) {
      showToast('Author name is required', 'error');
      return;
    }
    if (formData.paragraphs.length === 0) {
      showToast('At least one paragraph is required', 'error');
      return;
    }
    if (formData.paragraphs.some((p) => !p.content.trim())) {
      showToast('All paragraphs must have content', 'error');
      return;
    }
    if (status === 'published' && !imagePreview) {
      showToast(
        'A background image is required before publishing. Please upload one.',
        'warning'
      );
      return;
    }

    try {
      setSaveLoading(true);
      const submitData = { ...formData, status };

      if (drawerMode === 'add') {
        await create.mutateAsync({
          formData: submitData,
          userId: currentUser?.uid || '',
          backgroundImage,
        });
        showToast(
          `Blog ${status === 'published' ? 'published' : 'saved as draft'} successfully`,
          'success'
        );
      } else if (selectedBlog) {
        await update.mutateAsync({
          blogId: selectedBlog.id!,
          formData: submitData,
          backgroundImage,
          removeImage,
        });
        showToast('Blog updated successfully', 'success');
      }

      setDrawerOpen(false);
      resetForm();
      resetToFirstPage();
    } catch (err: any) {
      console.error('Error saving blog:', err);
      showToast(`Failed to save blog: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // ── Access denied — no blog.view permission ───────────────────────────────
  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <ArticleIcon
              sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }}
            />
            <Typography variant="h5" gutterBottom>
              Access Denied
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}
            >
              You don't have permission to view blogs. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Initial full-page loading state ───────────────────────────────────────
  if (blogsLoading && blogs.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading blogs...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <BorderColorIcon
              sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}
              >
                Blog Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create and manage your blog posts
              </Typography>
            </Box>
          </Stack>

          {/* Create Blog button — only shown when user has blog.create */}
          {canCreate && (
            <Button
              onClick={handleAddBlog}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
            >
              Create Blog
            </Button>
          )}
        </Stack>

        {/* ── Stats Cards ── */}
        <Grid
          container
          spacing={{ xs: 1, sm: 1.5, md: 2 }}
          sx={{ mb: 2 }}
          alignItems="stretch"
        >
          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Total Blogs
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}
                >
                  {totalBlogs}
                </Typography>
                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" color="success.main" noWrap>
                      {totalPublished} published
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'warning.main',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" color="warning.main" noWrap>
                      {totalDrafts} drafts
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Published
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}
                >
                  {totalPublished}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalBlogs > 0
                      ? `${((totalPublished / totalBlogs) * 100).toFixed(1)}% of total`
                      : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Drafts
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}
                >
                  {totalDrafts}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalBlogs > 0
                      ? `${((totalDrafts / totalBlogs) * 100).toFixed(1)}% of total`
                      : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  Publish Rate
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}
                >
                  {totalBlogs > 0
                    ? `${((totalPublished / totalBlogs) * 100).toFixed(0)}%`
                    : '—'}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    of blogs are live
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search blogs... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button
                  onClick={clearFilters}
                  variant="text"
                  size="small"
                  startIcon={<ClearIcon />}
                >
                  Clear
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="published">
                        Published Only ({totalPublished})
                      </MenuItem>
                      <MenuItem value="draft">Drafts Only ({totalDrafts})</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${blogs.length} blogs matching your filters`
                        : `Showing ${blogs.length} blogs on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {blogs.length === 0 && !loading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
            }}
          >
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <ArticleIcon
                  sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }}
                />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Blogs Match Your Filters' : 'No Blogs Found'}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
                >
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : "You haven't created any blogs yet. Start by creating your first blog post."}
                </Typography>
                {hasActiveFilters ? (
                  <Button
                    onClick={clearFilters}
                    variant="outlined"
                    startIcon={<ClearIcon />}
                  >
                    Clear Filters
                  </Button>
                ) : (
                  // Only show Create Blog CTA when user has blog.create
                  canCreate && (
                    <Button
                      onClick={handleAddBlog}
                      variant="contained"
                      startIcon={<AddIcon />}
                    >
                      Create Blog
                    </Button>
                  )
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {blogs.length > 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: 'auto', md: 0 },
            }}
          >
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 480, md: 900 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      Author
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      Tags
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      Updated
                    </TableCell>
                    {/* Comments column — only shown when user has blog.comments.manage */}
                    {canManageComments && (
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        Comments
                      </TableCell>
                    )}
                    {/* Actions column — only shown if user has at least one action permission */}
                    {(canPublish || canManageComments || canEdit || canDelete) && (
                      <TableCell align="right">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blogs.map((blog) => (
                    <TableRow
                      key={blog.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}
                    >
                      {/* Title */}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {blog.backgroundImageUrl ? (
                            <Box
                              component="img"
                              src={blog.backgroundImageUrl}
                              alt={blog.title}
                              sx={{
                                width: { xs: 36, sm: 50 },
                                height: { xs: 36, sm: 50 },
                                borderRadius: 1,
                                objectFit: 'cover',
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <ArticleIcon color="primary" sx={{ flexShrink: 0 }} />
                          )}
                          <Box minWidth={0}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                              }}
                              noWrap
                            >
                              {blog.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {blog.paragraphs.length} paragraph
                              {blog.paragraphs.length !== 1 ? 's' : ''}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: { xs: 'block', sm: 'none' } }}
                            >
                              {blog.authorName}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Author */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2">{blog.authorName}</Typography>
                      </TableCell>

                      {/* Tags */}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {blog.tags.slice(0, 3).map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={getSoftChipSx('default', isDark)}
                            />
                          ))}
                          {blog.tags.length > 3 && (
                            <Chip
                              label={`+${blog.tags.length - 3}`}
                              size="small"
                              variant="outlined"
                              sx={getSoftChipSx('secondary', isDark)}
                            />
                          )}
                        </Stack>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={blog.status === 'published' ? 'Published' : 'Draft'}
                          size="small"
                          variant="outlined"
                          icon={
                            blog.status === 'published' ? <PublishIcon /> : <DraftIcon />
                          }
                          sx={getSoftChipSx(
                            blog.status === 'published' ? 'success' : 'warning',
                            isDark
                          )}
                        />
                      </TableCell>

                      {/* Updated */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {blog.updatedAt?.toLocaleDateString()}
                        </Typography>
                      </TableCell>

                      {/* Comments cell — only rendered when user has blog.comments.manage */}
                      {canManageComments && (
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          {blog.id && commentCounts[blog.id] !== undefined ? (
                            <Chip
                              icon={
                                <CommentIcon sx={{ fontSize: '14px !important' }} />
                              }
                              label={commentCounts[blog.id]}
                              size="small"
                              variant="outlined"
                              sx={getSoftChipSx(
                                commentCounts[blog.id] > 0 ? 'info' : 'default',
                                isDark
                              )}
                              onClick={() => handleViewComments(blog)}
                              clickable
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      )}

                      {/* Actions — each button shown only when the user has the relevant permission */}
                      {(canPublish || canManageComments || canEdit || canDelete) && (
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                            flexWrap="nowrap"
                          >
                            {/* Publish / Move to Draft — requires blog.publish */}
                            {canPublish && (
                              <Tooltip
                                title={
                                  blog.status === 'published'
                                    ? 'Move to Draft'
                                    : 'Publish'
                                }
                              >
                                <IconButton
                                  onClick={() => handleToggleStatus(blog)}
                                  size="small"
                                  color={
                                    blog.status === 'published' ? 'warning' : 'success'
                                  }
                                >
                                  {blog.status === 'published' ? (
                                    <DraftIcon fontSize="small" />
                                  ) : (
                                    <PublishIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}

                            {/* Manage Comments — requires blog.comments.manage */}
                            {canManageComments && (
                              <Tooltip title="Manage Comments">
                                <IconButton
                                  onClick={() => handleViewComments(blog)}
                                  size="small"
                                  color="info"
                                >
                                  <Badge
                                    badgeContent={
                                      blog.id ? commentCounts[blog.id] : 0
                                    }
                                    color="primary"
                                    max={99}
                                    sx={{
                                      '& .MuiBadge-badge': {
                                        fontSize: '0.6rem',
                                        height: 16,
                                        minWidth: 16,
                                      },
                                    }}
                                  >
                                    <CommentIcon fontSize="small" />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                            )}

                            {/* Edit — requires blog.edit */}
                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton
                                  onClick={() => handleEditBlog(blog)}
                                  size="small"
                                  color="primary"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}

                            {/* Delete — requires blog.delete */}
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleDeleteBlog(blog)}
                                  disabled={deleteLoading === blog.id}
                                  size="small"
                                  color="error"
                                >
                                  {deleteLoading === blog.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            {/* Pagination */}
            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Showing{' '}
                    {totalBlogs === 0 ? 0 : (currentPage - 1) * BLOGS_PER_PAGE + 1}–
                    {Math.min(currentPage * BLOGS_PER_PAGE, totalBlogs)} of {totalBlogs}{' '}
                    blogs
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Blog Form Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 600 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <BorderColorIcon color="primary" />
              <Typography variant="h6">
                {drawerMode === 'add' ? 'Create New Blog' : 'Edit Blog'}
              </Typography>
            </Stack>
            <IconButton onClick={handleCloseDrawer} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <Box sx={{ px: 3, pt: 3, flexShrink: 0 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Blog Title"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, title: e.target.value }))
                    }
                    required
                    placeholder="Enter blog title"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Author Name"
                    value={formData.authorName}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, authorName: e.target.value }))
                    }
                    required
                    placeholder="Enter author name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Background Image
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 2 }}
                    >
                      Upload a background image for this blog post
                    </Typography>
                    {imagePreview ? (
                      <Box>
                        <Box
                          component="img"
                          src={imagePreview}
                          alt="Background preview"
                          sx={{
                            width: '100%',
                            maxHeight: 160,
                            objectFit: 'cover',
                            borderRadius: 1,
                            mb: 1,
                          }}
                        />
                        <Button
                          onClick={handleRemoveImage}
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          fullWidth
                        >
                          Remove Image
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        fullWidth
                      >
                        Upload Background Image
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageSelect}
                        />
                      </Button>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2">Content Paragraphs</Typography>
                    <Button
                      onClick={handleAddParagraph}
                      startIcon={<AddIcon />}
                      size="small"
                      variant="outlined"
                    >
                      Add Paragraph
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            <Box
              ref={paragraphsContainerRef}
              sx={{
                flex: 1,
                overflow: 'auto',
                px: 3,
                pb: 3,
                minHeight: 0,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
              }}
            >
              {formData.paragraphs.length === 0 ? (
                <Paper
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No paragraphs yet. Click "Add Paragraph" to start writing.
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={1.5}>
                  {formData.paragraphs.map((paragraph, index) => (
                    <Paper
                      key={paragraph.id}
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 1 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Stack sx={{ flexShrink: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveParagraph(paragraph.id, 'up')}
                            disabled={index === 0}
                          >
                            <DragIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveParagraph(paragraph.id, 'down')}
                            disabled={index === formData.paragraphs.length - 1}
                          >
                            <DragIcon
                              fontSize="small"
                              sx={{ transform: 'rotate(180deg)' }}
                            />
                          </IconButton>
                        </Stack>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          placeholder={`Paragraph ${index + 1}`}
                          value={paragraph.content}
                          onChange={(e) =>
                            handleParagraphChange(paragraph.id, e.target.value)
                          }
                          autoFocus={index === formData.paragraphs.length - 1}
                        />
                        <Tooltip title="Remove Paragraph">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteParagraph(paragraph.id)}
                            sx={{ flexShrink: 0 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}

              <Box
                sx={{
                  mt: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Tags (Max 10)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter tag and press Enter"
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  disabled={formData.tags.length >= 10}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          onClick={handleAddTag}
                          disabled={
                            !currentTag.trim() || formData.tags.length >= 10
                          }
                          size="small"
                        >
                          Add
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: formData.tags.length > 0 ? 2 : 0 }}
                />
                {formData.tags.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {formData.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        onDelete={() => handleRemoveTag(tag)}
                        variant="outlined"
                        sx={getSoftChipSx('primary', isDark)}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Drawer footer — Save as Draft hidden if !canCreate (add) or !canEdit (edit).
              Publish hidden if !canPublish, regardless of mode. */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              gap: 1.5,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              flexShrink: 0,
            }}
          >
            <Button
              onClick={handleCloseDrawer}
              disabled={saveLoading}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>

            {/* Save as Draft:
                - In add mode: visible only when canCreate
                - In edit mode: visible only when canEdit */}
            {((drawerMode === 'add' && canCreate) ||
              (drawerMode === 'edit' && canEdit)) && (
              <Button
                onClick={() => handleSubmit('draft')}
                variant="outlined"
                disabled={saveLoading}
                startIcon={
                  saveLoading ? <CircularProgress size={16} /> : <DraftIcon />
                }
              >
                Save as Draft
              </Button>
            )}

            {/* Publish: requires both the create/edit permission AND blog.publish */}
            {canPublish &&
              ((drawerMode === 'add' && canCreate) ||
                (drawerMode === 'edit' && canEdit)) && (
                <Button
                  onClick={() => handleSubmit('published')}
                  variant="contained"
                  disabled={saveLoading}
                  startIcon={
                    saveLoading ? <CircularProgress size={16} /> : <PublishIcon />
                  }
                >
                  Publish
                </Button>
              )}
          </Box>
        </Box>
      </Drawer>

      {/* ── Comments Management Drawer ── */}
      {canManageComments && (
        <BlogCommentManagement
          open={commentsDrawerOpen}
          blog={commentsBlog}
          onClose={handleCloseCommentsDrawer}
        />
      )}

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={!!deleteTarget}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Blog</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>"{deleteTarget?.title}"</strong>?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={
              deleteDialogLoading ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full-screen operation overlay ── */}
      <Backdrop
        open={saveLoading || deleteDialogLoading}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 200,
          flexDirection: 'column',
          gap: 3,
          bgcolor: 'rgba(0,0,0,0.6)',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}
            >
              {deleteDialogLoading ? 'Deleting Blog…' : saveAction}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
            >
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default BlogManagement;


// // src/admin/pages/blogs/BlogManagement.tsx
// import React, { useState, useRef } from 'react';
// import {
//   Box,
//   Button,
//   Card,
//   CardContent,
//   Drawer,
//   Divider,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogContentText,
//   DialogTitle,
//   FormControl,
//   Grid,
//   IconButton,
//   InputLabel,
//   MenuItem,
//   Paper,
//   Select,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   TextField,
//   Typography,
//   Alert,
//   CircularProgress,
//   Stack,
//   Chip,
//   Tooltip,
//   InputAdornment,
//   Pagination,
//   Collapse,
//   useTheme,
//   useMediaQuery,
//   Badge,
//   Backdrop,
//   LinearProgress,
// } from '@mui/material';
// import {
//   Add as AddIcon,
//   Edit as EditIcon,
//   Delete as DeleteIcon,
//   Article as ArticleIcon,
//   CloudUpload as CloudUploadIcon,
//   Cancel as CancelIcon,
//   Publish as PublishIcon,
//   DraftsOutlined as DraftIcon,
//   Close as CloseIcon,
//   DragIndicator as DragIcon,
//   Search as SearchIcon,
//   FilterList as FilterListIcon,
//   Clear as ClearIcon,
//   ExpandMore as ExpandMoreIcon,
//   ExpandLess as ExpandLessIcon,
//   Comment as CommentIcon,
// } from '@mui/icons-material';
// import BorderColorIcon from '@mui/icons-material/BorderColor';
// import { Blog, BlogParagraph, BlogFormData } from './blog';
// import { useAuth } from '../../../contexts/AuthContext';
// import {
//   useBlogsPaginated,
//   useBlogStats,
//   useBlogCommentCounts,
//   useBlogMutations,
// } from '../../../hooks/useBlogs';
// import ToastNotification from '../common/Toastnotification';
// import BlogCommentManagement from './Blogcommentmanagement';

// const BLOGS_PER_PAGE = 10;

// interface ToastState {
//   open: boolean;
//   message: string;
//   severity: 'success' | 'error' | 'warning' | 'info';
// }

// const getSoftChipSx = (color: string, isDark: boolean = false) => {
//   type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

//   const lightPalette: Record<string, PaletteEntry> = {
//     warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
//     info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
//     primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
//     success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
//     error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
//     secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
//     default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
//   };

//   const darkPalette: Record<string, PaletteEntry> = {
//     warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
//     info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
//     primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
//     success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
//     error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
//     secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
//     default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
//   };

//   const palette = isDark ? darkPalette : lightPalette;
//   const p = palette[color] ?? palette.default;

//   return {
//     backgroundColor: p.bg,
//     borderColor: p.border,
//     color: p.text,
//     fontWeight: 600,
//     border: `1px solid ${p.border}`,
//     '& .MuiChip-icon': { color: p.iconColor },
//     '& .MuiChip-label': { color: p.text },
//   };
// };

// interface LoadingOverlayProps {
//   wrapperRef: React.RefObject<HTMLDivElement | null>;
// }

// const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
//   const [rect, setRect] = useState<DOMRect | null>(null);
//   const theme = useTheme();

//   const overlayBg = theme.palette.mode === 'dark'
//     ? 'rgba(26, 29, 36, 0.85)'
//     : 'rgba(255, 255, 255, 0.85)';

//   React.useEffect(() => {
//     const measure = () => {
//       if (wrapperRef.current) {
//         setRect(wrapperRef.current.getBoundingClientRect());
//       }
//     };

//     measure();

//     const el = wrapperRef.current;
//     window.addEventListener('resize', measure);
//     el?.addEventListener('scroll', measure);

//     return () => {
//       window.removeEventListener('resize', measure);
//       el?.removeEventListener('scroll', measure);
//     };
//   }, [wrapperRef]);

//   if (!rect) return null;

//   return (
//     <Box
//       sx={{
//         position: 'fixed',
//         top: rect.top,
//         left: rect.left,
//         width: rect.width,
//         height: rect.height,
//         backgroundColor: overlayBg,
//         backdropFilter: 'blur(2px)',
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'center',
//         zIndex: 1100,
//         pointerEvents: 'all',
//         borderRadius: 'inherit',
//       }}
//     >
//       <Box sx={{ textAlign: 'center' }}>
//         <CircularProgress size={40} />
//         <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
//           Loading blogs...
//         </Typography>
//       </Box>
//     </Box>
//   );
// };

// const BlogManagement: React.FC = () => {
//   const { currentUser } = useAuth();

//   // ── UI state ──────────────────────────────────────────────────────────────
//   const [drawerOpen, setDrawerOpen] = useState(false);
//   const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
//   const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
//   const [formData, setFormData] = useState<BlogFormData>({
//     title: '',
//     authorName: '',
//     paragraphs: [],
//     tags: [],
//     status: 'draft',
//   });
//   const [saveLoading, setSaveLoading] = useState(false);
//   const [deleteLoading, setDeleteLoading] = useState<string>('');
//   const [backgroundImage, setBackgroundImage] = useState<File | null>(null);
//   const [imagePreview, setImagePreview] = useState<string>('');
//   const [removeImage, setRemoveImage] = useState(false);
//   const [currentTag, setCurrentTag] = useState('');

//   const [toast, setToast] = useState<ToastState>({
//     open: false,
//     message: '',
//     severity: 'info',
//   });

//   const [deleteTarget, setDeleteTarget] = useState<Blog | null>(null);
//   const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

//   // ── Pagination & filter state ─────────────────────────────────────────────
//   // FIX: Removed all cursor state (lastVisible, firstVisible, cursors).
//   // Seek-based pagination only needs the current page number.
//   const [currentPage, setCurrentPage] = useState(1);
//   const [searchInput, setSearchInput] = useState('');
//   const [searchQuery, setSearchQuery] = useState('');
//   const [statusFilter, setStatusFilter] = useState<string>('all');
//   const [showFilters, setShowFilters] = useState(false);

//   const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
//   const [commentsBlog, setCommentsBlog] = useState<Blog | null>(null);

//   const containerRef = useRef<HTMLDivElement>(null);
//   const paragraphsContainerRef = useRef<HTMLDivElement>(null);
//   const tableWrapperRef = useRef<HTMLDivElement>(null);

//   const theme = useTheme();
//   const isDark = theme.palette.mode === 'dark';
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
//   const saveAction = drawerMode === 'add' ? 'Creating Blog…' : 'Updating Blog…';


//   React.useEffect(() => {
//     if (paragraphsContainerRef.current && formData.paragraphs.length > 0) {
//       paragraphsContainerRef.current.scrollTo({
//         top: paragraphsContainerRef.current.scrollHeight,
//         behavior: 'smooth',
//       });
//     }
//   }, [formData.paragraphs.length]);

//   // ── React Query hooks ─────────────────────────────────────────────────────
//   // FIX: No longer passes a `cursors` object — just page number + filters.
//   const {
//     data: blogsData,
//     isLoading: blogsLoading,
//   isFetching: blogsIsFetching,  // add this
//     isError: blogsIsError,
//     error: blogsErrorObj,
//   } = useBlogsPaginated({
//     page: currentPage,
//     search: searchQuery,
//     status: statusFilter,
//     pageSize: BLOGS_PER_PAGE,
//   });

//   const { data: statsData } = useBlogStats();

//   const { create, update, remove, toggleStatus } = useBlogMutations();

//   const blogs = blogsData?.blogs ?? [];
//   const totalBlogs = blogsData?.totalCount ?? 0;
//   const totalPages = Math.ceil(totalBlogs / BLOGS_PER_PAGE);
//   const totalPublished = statsData?.published ?? 0;
//   const totalDrafts = statsData?.drafts ?? 0;
// const loading = blogsLoading || blogsIsFetching;

//   const blogIds = blogs.map(b => b.id!).filter(Boolean);
//   const { data: commentCounts = {}, refetch: refetchCommentCounts } = useBlogCommentCounts(blogIds);

//   // ── Toast helper ──────────────────────────────────────────────────────────
//   const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
//     setToast({ open: true, message, severity });
//   };
//   const handleCloseToast = () => setToast(t => ({ ...t, open: false }));

//   // ── Reset to page 1 ───────────────────────────────────────────────────────
//   // FIX: Simplified — no cursor state to reset, just set page to 1.
//   const resetToFirstPage = () => {
//     setCurrentPage(1);
//   };

//   // ── Pagination handler ────────────────────────────────────────────────────
//   // FIX: Simplified — just set the page number directly.
//   const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
//     setCurrentPage(page);
//   };

//   // ── Search ────────────────────────────────────────────────────────────────
//   const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
//     if (e.key === 'Enter') {
//       e.preventDefault();
//       setSearchQuery(searchInput);
//       resetToFirstPage();
//     }
//   };

//   // ── Status filter ─────────────────────────────────────────────────────────
//   const handleStatusFilterChange = (value: string) => {
//     setStatusFilter(value);
//     resetToFirstPage();
//   };

//   // ── Clear filters ─────────────────────────────────────────────────────────
//   const clearFilters = () => {
//     setSearchInput('');
//     setSearchQuery('');
//     setStatusFilter('all');
//     resetToFirstPage();
//     showToast('Filters cleared', 'info');
//   };

//   const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';
//   const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all'].filter(Boolean).length;

//   // ── Form helpers ──────────────────────────────────────────────────────────
//   const resetForm = () => {
//     setFormData({ title: '', authorName: '', paragraphs: [], tags: [], status: 'draft' });
//     setSelectedBlog(null);
//     setBackgroundImage(null);
//     setImagePreview('');
//     setRemoveImage(false);
//     setCurrentTag('');
//   };

//   const handleAddBlog = () => {
//     resetForm();
//     setDrawerMode('add');
//     setDrawerOpen(true);
//   };

//   const handleEditBlog = (blog: Blog) => {
//     setFormData({
//       title: blog.title,
//       authorName: blog.authorName,
//       paragraphs: blog.paragraphs,
//       tags: blog.tags,
//       status: blog.status,
//     });
//     setSelectedBlog(blog);
//     setImagePreview(blog.backgroundImageUrl || '');
//     setBackgroundImage(null);
//     setRemoveImage(false);
//     setDrawerMode('edit');
//     setDrawerOpen(true);
//   };

//   const handleCloseDrawer = () => {
//     setDrawerOpen(false);
//     resetForm();
//   };

//   // ── Comments Drawer helpers ───────────────────────────────────────────────
//   const handleViewComments = (blog: Blog) => {
//     setCommentsBlog(blog);
//     setCommentsDrawerOpen(true);
//   };

//   const handleCloseCommentsDrawer = () => {
//     setCommentsDrawerOpen(false);
//     setCommentsBlog(null);
//     if (blogIds.length > 0) refetchCommentCounts();
//   };

//   // ── Delete ────────────────────────────────────────────────────────────────
//   const handleDeleteBlog = (blog: Blog) => {
//     setDeleteTarget(blog);
//   };

//   const handleDeleteConfirm = async () => {
//     if (!deleteTarget) return;
//     try {
//       setDeleteDialogLoading(true);
//       setDeleteLoading(deleteTarget.id!);
//       await remove.mutateAsync(deleteTarget.id!);
//       setDeleteTarget(null);
//       resetToFirstPage();
//       showToast('Blog deleted successfully', 'success');
//     } catch (err: any) {
//       console.error('Error deleting blog:', err);
//       showToast(`Failed to delete blog: ${err.message || 'Unknown error'}`, 'error');
//     } finally {
//       setDeleteDialogLoading(false);
//       setDeleteLoading('');
//     }
//   };

//   const handleDeleteCancel = () => {
//     setDeleteTarget(null);
//   };

//   // ── Toggle status ──────────────────────────────────────────────────────────
//   const handleToggleStatus = async (blog: Blog) => {
//     const newStatus = blog.status === 'draft' ? 'published' : 'draft';
//     try {
//       await toggleStatus.mutateAsync({ blogId: blog.id!, status: newStatus });
//       resetToFirstPage();
//       showToast(
//         `Blog ${newStatus === 'published' ? 'published' : 'moved to draft'} successfully`,
//         'success'
//       );
//     } catch (err: any) {
//       console.error('Error toggling blog status:', err);
//       showToast(`Failed to update blog status: ${err.message || 'Unknown error'}`, 'error');
//     }
//   };

//   // ── Image helpers ─────────────────────────────────────────────────────────
//   const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const file = e.target.files?.[0];
//     if (file) {
//       if (!file.type.startsWith('image/')) {
//         showToast('Please select an image file', 'error');
//         return;
//       }
//       if (file.size > 5 * 1024 * 1024) {
//         showToast('Image size should be less than 5MB', 'error');
//         return;
//       }
//       setBackgroundImage(file);
//       setRemoveImage(false);
//       const reader = new FileReader();
//       reader.onloadend = () => {
//         setImagePreview(reader.result as string);
//       };
//       reader.readAsDataURL(file);
//     }
//   };

//   const handleRemoveImage = () => {
//     setImagePreview('');
//     setBackgroundImage(null);
//     setRemoveImage(true);
//   };

//   // ── Paragraph helpers ─────────────────────────────────────────────────────
//   const handleAddParagraph = () => {
//     const newParagraph: BlogParagraph = {
//       id: Date.now().toString(),
//       content: '',
//       order: formData.paragraphs.length,
//     };
//     setFormData(prev => ({ ...prev, paragraphs: [...prev.paragraphs, newParagraph] }));
//   };

//   const handleParagraphChange = (id: string, content: string) => {
//     setFormData(prev => ({
//       ...prev,
//       paragraphs: prev.paragraphs.map(p => (p.id === id ? { ...p, content } : p)),
//     }));
//   };

//   const handleDeleteParagraph = (id: string) => {
//     setFormData(prev => ({
//       ...prev,
//       paragraphs: prev.paragraphs
//         .filter(p => p.id !== id)
//         .map((p, index) => ({ ...p, order: index })),
//     }));
//   };

//   const handleMoveParagraph = (id: string, direction: 'up' | 'down') => {
//     const index = formData.paragraphs.findIndex(p => p.id === id);
//     if (index === -1) return;
//     if (direction === 'up' && index === 0) return;
//     if (direction === 'down' && index === formData.paragraphs.length - 1) return;
//     const newParagraphs = [...formData.paragraphs];
//     const targetIndex = direction === 'up' ? index - 1 : index + 1;
//     [newParagraphs[index], newParagraphs[targetIndex]] = [newParagraphs[targetIndex], newParagraphs[index]];
//     setFormData(prev => ({
//       ...prev,
//       paragraphs: newParagraphs.map((p, idx) => ({ ...p, order: idx })),
//     }));
//   };

//   // ── Tag helpers ───────────────────────────────────────────────────────────
//   const handleAddTag = () => {
//     if (!currentTag.trim()) return;
//     if (formData.tags.length >= 10) {
//       showToast('Maximum 10 tags allowed', 'warning');
//       return;
//     }
//     if (formData.tags.includes(currentTag.trim())) {
//       showToast('Tag already exists', 'warning');
//       return;
//     }
//     setFormData(prev => ({ ...prev, tags: [...prev.tags, currentTag.trim()] }));
//     setCurrentTag('');
//   };

//   const handleRemoveTag = (tag: string) => {
//     setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
//   };

//   // ── Form submit ───────────────────────────────────────────────────────────
//   const handleSubmit = async (status: 'draft' | 'published') => {
//     if (!formData.title.trim()) {
//       showToast('Blog title is required', 'error');
//       return;
//     }
//     if (!formData.authorName.trim()) {
//       showToast('Author name is required', 'error');
//       return;
//     }
//     if (formData.paragraphs.length === 0) {
//       showToast('At least one paragraph is required', 'error');
//       return;
//     }
//     if (formData.paragraphs.some(p => !p.content.trim())) {
//       showToast('All paragraphs must have content', 'error');
//       return;
//     }
//     if (status === 'published' && !imagePreview) {
//       showToast('A background image is required before publishing. Please upload one.', 'warning');
//       return;
//     }

//     try {
//       setSaveLoading(true);
//       const submitData = { ...formData, status };

//       if (drawerMode === 'add') {
//         await create.mutateAsync({
//           formData: submitData,
//           userId: currentUser?.uid || '',
//           backgroundImage,
//         });
//         showToast(
//           `Blog ${status === 'published' ? 'published' : 'saved as draft'} successfully`,
//           'success'
//         );
//       } else if (selectedBlog) {
//         await update.mutateAsync({
//           blogId: selectedBlog.id!,
//           formData: submitData,
//           backgroundImage,
//           removeImage,
//         });
//         showToast('Blog updated successfully', 'success');
//       }

//       setDrawerOpen(false);
//       resetForm();
//       resetToFirstPage();
//     } catch (err: any) {
//       console.error('Error saving blog:', err);
//       showToast(`Failed to save blog: ${err.message || 'Unknown error'}`, 'error');
//     } finally {
//       setSaveLoading(false);
//     }
//   };

//   // ── Initial full-page loading state ───────────────────────────────────────
//   if (loading && blogs.length === 0) {
//     return (
//       <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
//         <CircularProgress size={40} />
//         <Typography variant="body1" sx={{ ml: 2 }}>Loading blogs...</Typography>
//       </Box>
//     );
//   }

//   return (
//     <Box
//       ref={containerRef}
//       sx={{
//         height: { md: '100%' },
//         display: 'flex',
//         flexDirection: 'column',
//         overflow: { xs: 'visible', md: 'hidden' },
//         p: { xs: 2, sm: 3 },
//       }}
//     >
//       {/* ── Fixed Header ── */}
//       <Box sx={{ flexShrink: 0 }}>
//         <Stack
//           direction={{ xs: 'column', sm: 'row' }}
//           justifyContent="space-between"
//           alignItems={{ xs: 'flex-start', sm: 'center' }}
//           spacing={2}
//           sx={{ mb: 3 }}
//         >
//           <Stack direction="row" alignItems="center" spacing={2}>
//             <BorderColorIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
//             <Box>
//               <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
//                 Blog Management
//               </Typography>
//               <Typography variant="body2" color="text.secondary">
//                 Create and manage your blog posts
//               </Typography>
//             </Box>
//           </Stack>
//           <Button
//             onClick={handleAddBlog}
//             variant="contained"
//             startIcon={<AddIcon />}
//             sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
//           >
//             Create Blog
//           </Button>
//         </Stack>

//         {/* {blogsIsError && (
//           <Alert severity="error" sx={{ mb: 3 }}>
//             {(blogsErrorObj as Error)?.message || 'Failed to fetch blogs'}
//           </Alert>
//         )} */}

//         {/* ── Stats Cards ── */}
//         <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
//           <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
//             <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//               <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                 <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total Blogs</Typography>
//                 <Typography variant="h5" component="div" color="text.primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
//                   {totalBlogs}
//                 </Typography>
//                 <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                     <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
//                     <Typography variant="caption" color="success.main" noWrap>{totalPublished} published</Typography>
//                   </Box>
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                     <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }} />
//                     <Typography variant="caption" color="warning.main" noWrap>{totalDrafts} drafts</Typography>
//                   </Box>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
//             <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//               <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                 <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Published</Typography>
//                 <Typography variant="h5" component="div" color="text.primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
//                   {totalPublished}
//                 </Typography>
//                 <Box sx={{ mt: 'auto' }}>
//                   <Typography variant="caption" color="text.secondary" noWrap>
//                     {totalBlogs > 0 ? `${((totalPublished / totalBlogs) * 100).toFixed(1)}% of total` : '—'}
//                   </Typography>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
//             <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//               <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                 <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Drafts</Typography>
//                 <Typography variant="h5" component="div" color="text.primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
//                   {totalDrafts}
//                 </Typography>
//                 <Box sx={{ mt: 'auto' }}>
//                   <Typography variant="caption" color="text.secondary" noWrap>
//                     {totalBlogs > 0 ? `${((totalDrafts / totalBlogs) * 100).toFixed(1)}% of total` : '—'}
//                   </Typography>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>

//           <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
//             <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//               <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                 <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Publish Rate</Typography>
//                 <Typography variant="h5" component="div" color="text.primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
//                   {totalBlogs > 0 ? `${((totalPublished / totalBlogs) * 100).toFixed(0)}%` : '—'}
//                 </Typography>
//                 <Box sx={{ mt: 'auto' }}>
//                   <Typography variant="caption" color="text.secondary" noWrap>of blogs are live</Typography>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {/* ── Search & Filters ── */}
//         <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
//           <Stack
//             direction={{ xs: 'column', sm: 'row' }}
//             spacing={2}
//             alignItems={{ xs: 'stretch', sm: 'center' }}
//             justifyContent="space-between"
//           >
//             <TextField
//               placeholder="Search blogs... (Press Enter)"
//               value={searchInput}
//               onChange={(e) => setSearchInput(e.target.value)}
//               onKeyPress={handleSearchKeyPress}
//               sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
//               size="small"
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start">
//                     <SearchIcon />
//                   </InputAdornment>
//                 ),
//               }}
//             />
//             <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
//               <Button
//                 onClick={() => setShowFilters(!showFilters)}
//                 variant={hasActiveFilters ? 'contained' : 'outlined'}
//                 startIcon={<FilterListIcon />}
//                 endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//                 size="small"
//               >
//                 Filters {hasActiveFilters && `(${activeFiltersCount})`}
//               </Button>
//               {hasActiveFilters && (
//                 <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
//                   Clear
//                 </Button>
//               )}
//             </Stack>
//           </Stack>

//           <Collapse in={showFilters}>
//             <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
//               <Grid container spacing={2}>
//                 <Grid item xs={12} sm={6} md={3}>
//                   <FormControl fullWidth size="small">
//                     <InputLabel>Status</InputLabel>
//                     <Select
//                       value={statusFilter}
//                       onChange={(e) => handleStatusFilterChange(e.target.value)}
//                       label="Status"
//                     >
//                       <MenuItem value="all">All Status</MenuItem>
//                       <MenuItem value="published">Published Only ({totalPublished})</MenuItem>
//                       <MenuItem value="draft">Drafts Only ({totalDrafts})</MenuItem>
//                     </Select>
//                   </FormControl>
//                 </Grid>
//                 <Grid item xs={12} md={9}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
//                     <Typography variant="body2" color="text.secondary">
//                       {hasActiveFilters
//                         ? `Found ${blogs.length} blogs matching your filters`
//                         : `Showing ${blogs.length} blogs on this page`}
//                     </Typography>
//                   </Box>
//                 </Grid>
//               </Grid>
//             </Box>
//           </Collapse>
//         </Paper>
//       </Box>

//       {/* ── Scrollable Body ── */}
//       <Box
//         sx={{
//           flex: 1,
//           overflow: { xs: 'visible', md: 'auto' },
//           display: 'flex',
//           flexDirection: 'column',
//           minHeight: { xs: 'auto', md: 0 },
//         }}
//       >
//         {blogs.length === 0 && !loading && (
//           <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
//             <Card sx={{ width: '100%', maxWidth: 500 }}>
//               <CardContent sx={{ textAlign: 'center', py: 6 }}>
//                 <ArticleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
//                 <Typography variant="h5" color="text.secondary" gutterBottom>
//                   {hasActiveFilters ? 'No Blogs Match Your Filters' : 'No Blogs Found'}
//                 </Typography>
//                 <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
//                   {hasActiveFilters
//                     ? 'Try adjusting your search or filter criteria.'
//                     : "You haven't created any blogs yet. Start by creating your first blog post."}
//                 </Typography>
//                 {hasActiveFilters ? (
//                   <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
//                     Clear Filters
//                   </Button>
//                 ) : (
//                   <Button onClick={handleAddBlog} variant="contained" startIcon={<AddIcon />}>
//                     Create Blog
//                   </Button>
//                 )}
//               </CardContent>
//             </Card>
//           </Box>
//         )}

//         {blogs.length > 0 && (
//           <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
//             <TableContainer
//               component={Paper}
//               ref={tableWrapperRef}
//               sx={{
//                 flex: 1,
//                 overflow: { xs: 'auto', md: 'auto' },
//                 maxHeight: { md: '100%' },
//                 mb: 2,
//                 position: 'relative',
//                 '& .MuiTable-root': { minWidth: { xs: 480, md: 900 } },
//               }}
//             >
//               <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
//                 <TableHead>
//                   <TableRow>
//                     <TableCell>Title</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Author</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Tags</TableCell>
//                     <TableCell>Status</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Updated</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Comments</TableCell>
//                     <TableCell align="right">Actions</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {blogs.map((blog) => (
//                     <TableRow
//                       key={blog.id}
//                       hover
//                       sx={{
//                         '&:hover': { backgroundColor: 'action.hover' },
//                         '&:last-child td, &:last-child th': { borderBottom: 0 },
//                       }}
//                     >
//                       <TableCell>
//                         <Stack direction="row" alignItems="center" spacing={1}>
//                           {blog.backgroundImageUrl ? (
//                             <Box
//                               component="img"
//                               src={blog.backgroundImageUrl}
//                               alt={blog.title}
//                               sx={{
//                                 width: { xs: 36, sm: 50 },
//                                 height: { xs: 36, sm: 50 },
//                                 borderRadius: 1,
//                                 objectFit: 'cover',
//                                 flexShrink: 0,
//                               }}
//                             />
//                           ) : (
//                             <ArticleIcon color="primary" sx={{ flexShrink: 0 }} />
//                           )}
//                           <Box minWidth={0}>
//                             <Typography
//                               variant="subtitle2"
//                               sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
//                               noWrap
//                             >
//                               {blog.title}
//                             </Typography>
//                             <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
//                               {blog.paragraphs.length} paragraph{blog.paragraphs.length !== 1 ? 's' : ''}
//                             </Typography>
//                             <Typography
//                               variant="caption"
//                               color="text.secondary"
//                               sx={{ display: { xs: 'block', sm: 'none' } }}
//                             >
//                               {blog.authorName}
//                             </Typography>
//                           </Box>
//                         </Stack>
//                       </TableCell>

//                       <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
//                         <Typography variant="body2">{blog.authorName}</Typography>
//                       </TableCell>

//                       <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
//                         <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
//                           {blog.tags.slice(0, 3).map((tag, index) => (
//                             <Chip
//                               key={index}
//                               label={tag}
//                               size="small"
//                               variant="outlined"
//                               sx={getSoftChipSx('default', isDark)}
//                             />
//                           ))}
//                           {blog.tags.length > 3 && (
//                             <Chip
//                               label={`+${blog.tags.length - 3}`}
//                               size="small"
//                               variant="outlined"
//                               sx={getSoftChipSx('secondary', isDark)}
//                             />
//                           )}
//                         </Stack>
//                       </TableCell>

//                       <TableCell>
//                         <Chip
//                           label={blog.status === 'published' ? 'Published' : 'Draft'}
//                           size="small"
//                           variant="outlined"
//                           icon={blog.status === 'published' ? <PublishIcon /> : <DraftIcon />}
//                           sx={getSoftChipSx(blog.status === 'published' ? 'success' : 'warning', isDark)}
//                         />
//                       </TableCell>

//                       <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
//                         <Typography variant="body2" color="text.secondary">
//                           {blog.updatedAt?.toLocaleDateString()}
//                         </Typography>
//                       </TableCell>

//                       <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
//                         {blog.id && commentCounts[blog.id] !== undefined ? (
//                           <Chip
//                             icon={<CommentIcon sx={{ fontSize: '14px !important' }} />}
//                             label={commentCounts[blog.id]}
//                             size="small"
//                             variant="outlined"
//                             sx={getSoftChipSx(commentCounts[blog.id] > 0 ? 'info' : 'default', isDark)}
//                             onClick={() => handleViewComments(blog)}
//                             clickable
//                           />
//                         ) : (
//                           <Typography variant="caption" color="text.disabled">—</Typography>
//                         )}
//                       </TableCell>

//                       <TableCell align="right">
//                         <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
//                           <Tooltip title={blog.status === 'published' ? 'Move to Draft' : 'Publish'}>
//                             <IconButton
//                               onClick={() => handleToggleStatus(blog)}
//                               size="small"
//                               color={blog.status === 'published' ? 'warning' : 'success'}
//                             >
//                               {blog.status === 'published'
//                                 ? <DraftIcon fontSize="small" />
//                                 : <PublishIcon fontSize="small" />}
//                             </IconButton>
//                           </Tooltip>

//                           <Tooltip title="Manage Comments">
//                             <IconButton
//                               onClick={() => handleViewComments(blog)}
//                               size="small"
//                               color="info"
//                             >
//                               <Badge
//                                 badgeContent={blog.id ? commentCounts[blog.id] : 0}
//                                 color="primary"
//                                 max={99}
//                                 sx={{
//                                   '& .MuiBadge-badge': {
//                                     fontSize: '0.6rem',
//                                     height: 16,
//                                     minWidth: 16,
//                                   },
//                                 }}
//                               >
//                                 <CommentIcon fontSize="small" />
//                               </Badge>
//                             </IconButton>
//                           </Tooltip>

//                           <Tooltip title="Edit">
//                             <IconButton onClick={() => handleEditBlog(blog)} size="small" color="primary">
//                               <EditIcon fontSize="small" />
//                             </IconButton>
//                           </Tooltip>

//                           <Tooltip title="Delete">
//                             <IconButton
//                               onClick={() => handleDeleteBlog(blog)}
//                               disabled={deleteLoading === blog.id}
//                               size="small"
//                               color="error"
//                             >
//                               {deleteLoading === blog.id
//                                 ? <CircularProgress size={16} />
//                                 : <DeleteIcon fontSize="small" />}
//                             </IconButton>
//                           </Tooltip>
//                         </Stack>
//                       </TableCell>
//                     </TableRow>
//                   ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>

//             {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

//             <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
//               <Box sx={{ py: 2, px: 2 }}>
//                 <Stack
//                   direction={{ xs: 'column', sm: 'row' }}
//                   spacing={{ xs: 1.5, sm: 3 }}
//                   alignItems="center"
//                   justifyContent="space-between"
//                   sx={{ width: '100%' }}
//                 >
//                   <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
//                     Showing {totalBlogs === 0 ? 0 : (currentPage - 1) * BLOGS_PER_PAGE + 1}–
//                     {Math.min(currentPage * BLOGS_PER_PAGE, totalBlogs)} of {totalBlogs} blogs
//                   </Typography>
//                   <Pagination
//                     count={Math.max(1, totalPages)}
//                     page={currentPage}
//                     onChange={handlePageChange}
//                     color="primary"
//                     size={isMobile ? 'small' : 'medium'}
//                     showFirstButton
//                     showLastButton
//                     disabled={loading || totalPages <= 1}
//                   />
//                   <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
//                     Page {currentPage} of {Math.max(1, totalPages)}
//                   </Typography>
//                 </Stack>
//               </Box>
//             </Paper>
//           </Box>
//         )}
//       </Box>

//       {/* ── Blog Form Drawer ── */}
//       <Drawer
//         anchor="right"
//         open={drawerOpen}
//         onClose={handleCloseDrawer}
//         PaperProps={{ sx: { width: { xs: '100%', sm: 600 } } }}
//       >
//         <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
//           <Box
//             sx={{
//               px: 3, py: 2,
//               borderBottom: 1, borderColor: 'divider',
//               display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//               flexShrink: 0,
//             }}
//           >
//             <Stack direction="row" alignItems="center" spacing={1.5}>
//               <BorderColorIcon color="primary" />
//               <Typography variant="h6">
//                 {drawerMode === 'add' ? 'Create New Blog' : 'Edit Blog'}
//               </Typography>
//             </Stack>
//             <IconButton onClick={handleCloseDrawer} edge="end">
//               <CloseIcon />
//             </IconButton>
//           </Box>

//           <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
//             <Box sx={{ px: 3, pt: 3, flexShrink: 0 }}>
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <TextField
//                     fullWidth
//                     label="Blog Title"
//                     value={formData.title}
//                     onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
//                     required
//                     placeholder="Enter blog title"
//                   />
//                 </Grid>
//                 <Grid item xs={12}>
//                   <TextField
//                     fullWidth
//                     label="Author Name"
//                     value={formData.authorName}
//                     onChange={(e) => setFormData(prev => ({ ...prev, authorName: e.target.value }))}
//                     required
//                     placeholder="Enter author name"
//                   />
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 2 }}>
//                     <Typography variant="subtitle2" gutterBottom>Background Image</Typography>
//                     <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
//                       Upload a background image for this blog post
//                     </Typography>
//                     {imagePreview ? (
//                       <Box>
//                         <Box
//                           component="img"
//                           src={imagePreview}
//                           alt="Background preview"
//                           sx={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 1, mb: 1 }}
//                         />
//                         <Button
//                           onClick={handleRemoveImage}
//                           variant="outlined"
//                           color="error"
//                           size="small"
//                           startIcon={<DeleteIcon />}
//                           fullWidth
//                         >
//                           Remove Image
//                         </Button>
//                       </Box>
//                     ) : (
//                       <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth>
//                         Upload Background Image
//                         <input type="file" hidden accept="image/*" onChange={handleImageSelect} />
//                       </Button>
//                     )}
//                   </Box>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
//                     <Typography variant="subtitle2">Content Paragraphs</Typography>
//                     <Button onClick={handleAddParagraph} startIcon={<AddIcon />} size="small" variant="outlined">
//                       Add Paragraph
//                     </Button>
//                   </Stack>
//                 </Grid>
//               </Grid>
//             </Box>

//             <Box
//               ref={paragraphsContainerRef}
//               sx={{
//                 flex: 1, overflow: 'auto', px: 3, pb: 3, minHeight: 0,
//                 '&::-webkit-scrollbar': { width: '6px' },
//                 '&::-webkit-scrollbar-track': { background: 'transparent' },
//                 '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
//                 '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
//               }}
//             >
//               {formData.paragraphs.length === 0 ? (
//                 <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover', borderRadius: 1 }}>
//                   <Typography variant="body2" color="text.secondary">
//                     No paragraphs yet. Click "Add Paragraph" to start writing.
//                   </Typography>
//                 </Paper>
//               ) : (
//                 <Stack spacing={1.5}>
//                   {formData.paragraphs.map((paragraph, index) => (
//                     <Paper key={paragraph.id} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
//                       <Stack direction="row" spacing={1} alignItems="flex-start">
//                         <Stack sx={{ flexShrink: 0 }}>
//                           <IconButton
//                             size="small"
//                             onClick={() => handleMoveParagraph(paragraph.id, 'up')}
//                             disabled={index === 0}
//                           >
//                             <DragIcon fontSize="small" />
//                           </IconButton>
//                           <IconButton
//                             size="small"
//                             onClick={() => handleMoveParagraph(paragraph.id, 'down')}
//                             disabled={index === formData.paragraphs.length - 1}
//                           >
//                             <DragIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
//                           </IconButton>
//                         </Stack>
//                         <TextField
//                           fullWidth
//                           multiline
//                           rows={3}
//                           placeholder={`Paragraph ${index + 1}`}
//                           value={paragraph.content}
//                           onChange={(e) => handleParagraphChange(paragraph.id, e.target.value)}
//                           autoFocus={index === formData.paragraphs.length - 1}
//                         />
//                         <Tooltip title="Remove Paragraph">
//                           <IconButton
//                             size="small"
//                             color="error"
//                             onClick={() => handleDeleteParagraph(paragraph.id)}
//                             sx={{ flexShrink: 0 }}
//                           >
//                             <CloseIcon fontSize="small" />
//                           </IconButton>
//                         </Tooltip>
//                       </Stack>
//                     </Paper>
//                   ))}
//                 </Stack>
//               )}

//               <Box sx={{ mt: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
//                 <Typography variant="subtitle2" gutterBottom>Tags (Max 10)</Typography>
//                 <TextField
//                   fullWidth
//                   size="small"
//                   placeholder="Enter tag and press Enter"
//                   value={currentTag}
//                   onChange={(e) => setCurrentTag(e.target.value)}
//                   onKeyPress={(e) => {
//                     if (e.key === 'Enter') {
//                       e.preventDefault();
//                       handleAddTag();
//                     }
//                   }}
//                   disabled={formData.tags.length >= 10}
//                   InputProps={{
//                     endAdornment: (
//                       <InputAdornment position="end">
//                         <Button
//                           onClick={handleAddTag}
//                           disabled={!currentTag.trim() || formData.tags.length >= 10}
//                           size="small"
//                         >
//                           Add
//                         </Button>
//                       </InputAdornment>
//                     ),
//                   }}
//                   sx={{ mb: formData.tags.length > 0 ? 2 : 0 }}
//                 />
//                 {formData.tags.length > 0 && (
//                   <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
//                     {formData.tags.map((tag, index) => (
//                       <Chip
//                         key={index}
//                         label={tag}
//                         onDelete={() => handleRemoveTag(tag)}
//                         variant="outlined"
//                         sx={getSoftChipSx('primary', isDark)}
//                       />
//                     ))}
//                   </Stack>
//                 )}
//               </Box>
//             </Box>
//           </Box>

//           <Divider />
//           <Box
//             sx={{
//               px: 3, py: 2,
//               display: 'flex', gap: 1.5, justifyContent: 'flex-end',
//               flexWrap: 'wrap', flexShrink: 0,
//             }}
//           >
//             <Button onClick={handleCloseDrawer} disabled={saveLoading} startIcon={<CancelIcon />}>
//               Cancel
//             </Button>
//             <Button
//               onClick={() => handleSubmit('draft')}
//               variant="outlined"
//               disabled={saveLoading}
//               startIcon={saveLoading ? <CircularProgress size={16} /> : <DraftIcon />}
//             >
//               Save as Draft
//             </Button>
//             <Button
//               onClick={() => handleSubmit('published')}
//               variant="contained"
//               disabled={saveLoading}
//               startIcon={saveLoading ? <CircularProgress size={16} /> : <PublishIcon />}
//             >
//               Publish
//             </Button>
//           </Box>
//         </Box>
//       </Drawer>

//       {/* ── Comments Management Drawer ── */}
//       <BlogCommentManagement
//         open={commentsDrawerOpen}
//         blog={commentsBlog}
//         onClose={handleCloseCommentsDrawer}
//       />

//       {/* ── Delete Confirmation Dialog ── */}
//       <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
//         <DialogTitle>Delete Blog</DialogTitle>
//         <DialogContent>
//           <DialogContentText>
//             Are you sure you want to delete{' '}
//             <strong>"{deleteTarget?.title}"</strong>?{' '}
//             This action cannot be undone.
//           </DialogContentText>
//         </DialogContent>
//         <DialogActions sx={{ px: 3, pb: 2 }}>
//           <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>
//             Cancel
//           </Button>
//           <Button
//             variant="contained"
//             color="error"
//             onClick={handleDeleteConfirm}
//             disabled={deleteDialogLoading}
//             startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
//           >
//             Delete
//           </Button>
//         </DialogActions>
//       </Dialog>

//       {/* ── Full-screen operation overlay ───────────────────────────────────── */}
//       <Backdrop
//         open={saveLoading || deleteDialogLoading}
//         sx={{
//           zIndex: (t) => t.zIndex.drawer + 200,
//           flexDirection: 'column',
//           gap: 3,
//           bgcolor: 'rgba(0,0,0,0.6)',
//         }}
//       >
//         <Box
//           sx={{
//             bgcolor: 'background.paper',
//             borderRadius: 3,
//             px: 5,
//             py: 4,
//             display: 'flex',
//             flexDirection: 'column',
//             alignItems: 'center',
//             gap: 2.5,
//             minWidth: 280,
//             boxShadow: 24,
//           }}
//         >
//           <CircularProgress size={48} thickness={4} />
//           <Box sx={{ width: '100%' }}>
//             <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
//             <Typography
//               variant="subtitle1"
//               sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}
//             >
//               {deleteDialogLoading ? 'Deleting Blog…' : saveAction}
//             </Typography>
//             <Typography
//               variant="caption"
//               color="text.secondary"
//               sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
//             >
//               Please wait, do not close this window
//             </Typography>
//           </Box>
//         </Box>
//       </Backdrop>

//       <ToastNotification
//         open={toast.open}
//         message={toast.message}
//         severity={toast.severity}
//         onClose={handleCloseToast}
//       />
//     </Box>
//   );
// };

// export default BlogManagement;